/**
 * Web-Versionen der App vergleichen („8.0.0-pre.3356“) — EINE Regel für Server und App.
 *
 * Entstanden am 27.09.2026 in tools/changelog.ts (dort: „steht im CHANGELOG, läuft aber noch
 * nicht“). Am 05.10.2026 hierher gezogen (Ticket 2931): der Knopf „Was ist neu“ in der App fragt
 * dieselbe Frage — „ist dieser Satz neuer als das, was ich zuletzt angesehen habe?“ — und eine
 * zweite Schreibweise derselben Regel im Frontend wäre die Kopie, die auseinanderläuft.
 *
 * ⚠️ Nicht `vergleicheVersion` aus shared/app-version.ts: die kennt nur Ziffern und Punkte
 *    (Erweiterung, Mac-App). An „0-pre“ scheitert sie still (NaN) und meldet jede Version als neuer.
 */

/** „8.0.0-pre.3356“ → [8, 0, 0, 3356]; ohne „-pre.N“ zählt die Version als höher als jede pre. */
function versionsTeile(v: string): number[] | null {
  const m = /^(\d+)\.(\d+)\.(\d+)(?:-[a-z]+\.(\d+))?$/.exec(v.trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3]), m[4] === undefined ? Infinity : Number(m[4])];
}

/**
 * Ist das eine Version, die `istNeuerAls` vergleichen kann? Wer einen gemerkten Wert aus einem
 * Speicher liest, fragt das VORHER: `istNeuerAls` meldet bei Unlesbarem still „nein“, und ein
 * unlesbarer Merker hielte dann für immer alles für angesehen.
 */
export function istWebVersion(v: string | null | undefined): boolean {
  return !!v && versionsTeile(v) !== null;
}

/**
 * Ist `version` neuer als `laufend`? (27.09.2026)
 *
 * ⚠️ Der CHANGELOG wird bei jedem Aufruf frisch gelesen — ein Commit ohne
 *    Neustart steht also schon darin, sein Code läuft aber noch nicht.
 *    Vespera beschrieb damit Verhalten, das es noch nicht gab. Unlesbares
 *    zählt als „läuft“ — lieber nichts kennzeichnen als falsch.
 */
export function istNeuerAls(version: string, laufend: string | null | undefined): boolean {
  const a = versionsTeile(version);
  const b = laufend ? versionsTeile(laufend) : null;
  if (!a || !b) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}
